//Dependencies
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFilterKeyboardShortcuts } from '../../../../hooks/useFilterKeyboardShortcuts';
import { useTableCurrentPage } from '../../../../hooks/useTableCurrentPage';
import {
  createColumnHelper,
  ColumnFiltersState,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  getPaginationRowModel,
  PaginationState,
  VisibilityState,
  FilterFnOption,
  RowSelectionState,
} from '@tanstack/react-table';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, formatISO } from 'date-fns';
import isEqual from 'lodash.isequal';
import debug from 'debug';

const log = debug('rois:multimedia-table');

if (import.meta.env.DEV) {
  debug.enable('rois:multimedia-table*');
}

// Double Tap Support
import { DoubleTapRow } from '../components/DoubleTapRow';
import { SortableHeader } from '../components/SortableHeader';

// Styled Components
import {
  TrStyled,
  TableContainerStyled,
  SubContainerHead,
  ShowFiltersButton,
  ArrowBottomStyled,
  ContainerLoader,
  ContainerStyled,
  FiltersWithLoadingStyled,
} from './styles';
import {
  Th,
  Tbody,
  Td,
  Title,
  Table,
  TextCell,
  TextHeader,
  InputCheckContainer,
  CheckBoxStyled,
  InputCalendarStyled,
  SubContainerFilters,
  TitleInvisible,
  SearchableDropdownStyled,
  MultiSelectDropdownStyled,
  ContainerButtons,
} from '../components/MainStyledComponents';

// Components
import { PaginationButtons } from '../components/PaginationButtons';
import { SelectionBanner } from '../components/SelectionBanner';
import InputText from '../../../inputs/InputText';
import InputExpedient from '../../../inputs/InputExpedient';
import InputIndex from '../../../inputs/InputIndex';
import InputPK from '../../../inputs/InputPK';
import { DropDownVisibilityColumn } from '../components/DropDownVisibilityColumn';
import { createFlagColumn, exportIgnoreProps } from '../components/flagColumn';
import { useFlagScopeTexts } from '../../../../hooks/useFlagScopeTexts';
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';
import { SpecialTag } from '../../SpecialTag';

// Types
import {
  columnID,
  columnNames,
  getColumnNames,
  getColumnId,
} from '../components/types';
import { tableProps, dataTable, option } from './types';
import {
  Options,
  onChangeValue,
} from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';
import { ImperativeRefMultiSelect } from '../../../inputs/DropDown/MultiSelectDropdown/types';
import { ImperativeRefSearchableDropdown } from '../../../inputs/SearchableDropdown/types';
import {
  ImperativeRefCalendarInput,
  onChangeInputType,
} from '../../../inputs/Calendar/types';
import {
  ImperativeRefInputExpedient,
  onChangeInputExpedientTypes,
} from '../../../inputs/InputExpedient/types';
import {
  ImperativeRefInputIndex,
  onChangeInputIndexTypes,
} from '../../../inputs/InputIndex/types';
import {
  ImperativeRefInputPK,
  onChangeInputPKTypes,
} from '../../../inputs/InputPK/types';
import { Order } from '../../../../types/orders';
import { takeBase, tableIds } from '../types';
import { useServerPageSize } from '../utils/useServerPageSize';
import { useMirroredCursor } from '../utils/useMirroredCursor';
import { size, variant } from '../../SpecialTag/types';
import { Role } from '../../../../types/auth';
import { OrderState } from '../../../../types/orders';

// Mocks
import { mocksTable } from './mocks';

// Utils
import { roidFilterFunction, dateFilterFunction } from './utils';
import { formatRoidForDisplay } from '../utils/roidFormatter';
import { filterAndSortMultimediaUsers } from '../../../../utils/multimediaUserFilters';

// Media
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
} from '../../../../hooks/useMediaBreakPoints';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import {
  generateRoidPadding,
  getStateOptionsByRoleAndEntityType,
  getContainerTypeOptions,
} from '../../../../utils/order';
import { TABLE_ENTITY_TYPES, CONTAINER_SCOPED_TABLE_IDS, listingFiltersByRole } from '../types';

// Redux
import {
  getCursor,
  getIsLoadingData,
} from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';

// GQL
import { client } from '../../../../gql/client';
import {
  GET_CLASSIFICATION_BY_ID_OR_ROID,
  GET_SUB_CAPITOLS_BY_IDS,
} from '../../../../gql/queries/orders/predefinedData';
import { GET_ROADS_BY_IDS } from '../../../../gql/queries/geoPositions/roads';
import { Loader } from '../../../inputs/Loader';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import IncludeInactiveToggle from '../../../inputs/IncludeInactiveToggle';
import SearchableDropdown from '../../../inputs/SearchableDropdown';

// Columnas ordenables server-side → campo del backend (whitelist Order)
// ORDER_SORTABLE_FIELDS: registerInitDate, creationDate, state, pkInitKm, pkEndKm, roid, description
const SORTABLE_COLUMNS = {
  state: 'state',
  registerInitDate: 'registerInitDate',
  creationDate: 'creationDate',
  pkInit: 'pkInitKm',
  pkEnd: 'pkEndKm',
  roid: 'roid',
  description: 'description',
} as const;
const DEFAULT_SORT_FIELD = 'registerInitDate';

/**
 * Por defecto la bandera marca la Order de la fila: el listado de Multimedia
 * pide ORDERS (`FETCH_ORDERS`), así que `row.id` ya es el id de la Order. Fuera
 * del componente para que la referencia sea estable y no rehaga las columnas
 * en cada render.
 */
const defaultGetFlagOrderId = (row: dataTable): string | undefined =>
  row.id || undefined;

export const MultimediaCheckBoxTable = (allProps: tableProps): JSX.Element => {
  const {
    // Fuera de `props`: no son atributos del <table> (la tabla hace
    // `<Table {...props}>`).
    showFlagColumn = false,
    showOnlyFlaggedFilter = false,
    getFlagOrderId = defaultGetFlagOrderId,
    ...props
  } = allProps;
  const {
    idCss,
    titleText,
    data,
    onReFetchData,
    onReFetchPagination,
    tableId,
    selectionMode = 'radio',
    multimediaBasePath = '/work-orders/multimedia',
  } = props;
  const isCheckboxMode = selectionMode === 'checkbox';
  // Controlled-mode props (defined only when selectionMode === 'checkbox').
  // Cast via `as any` to bypass discriminated-union narrowing — same pattern as
  // ValidationsTable (the canonical reference for this signature).
  const controlledRowSelection = isCheckboxMode
    ? (props as any).rowSelection as RowSelectionState
    : undefined;
  const controlledOnRowSelectionChange = isCheckboxMode
    ? ((props as any).onRowSelectionChange as (
        u: RowSelectionState | ((o: RowSelectionState) => RowSelectionState)
      ) => void)
    : undefined;
  const selectAllMode: boolean = isCheckboxMode
    ? Boolean((props as any).selectAllMode)
    : false;
  const excludedIds: Set<string> | undefined = isCheckboxMode
    ? ((props as any).excludedIds as Set<string>)
    : undefined;
  const onSelectAll = isCheckboxMode
    ? ((props as any).onSelectAll as () => void)
    : undefined;
  const onClearSelection = isCheckboxMode
    ? ((props as any).onClearSelection as () => void)
    : undefined;
  const selectedCount: number = isCheckboxMode
    ? ((props as any).selectedCount as number) ?? 0
    : 0;
  // Radio-mode props (kept for back-compat)
  const setRowState = !isCheckboxMode
    ? ((props as any).setRowState as
        | React.Dispatch<React.SetStateAction<Array<string | undefined>>>
        | undefined)
    : undefined;
  const { t } = useTranslation(['tables', 'common']);
  const flagScopeTexts = useFlagScopeTexts();
  const dataAll = data?.dataOrderWithMultimedia ?? mocksTable;
  const globalCursor = useSelector(getCursor(tableId as string), isEqual);
  const isLoadingData = useSelector(getIsLoadingData(tableId), isEqual);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputListCapitolRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListSubcapitolRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListClassificationRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListUserRef = useRef<ImperativeRefMultiSelect>(null);
  const inputStateRef = useRef<ImperativeRefMultiSelect>(null);
  // Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen".
  const inputContainerTypeRef = useRef<ImperativeRefMultiSelect>(null);
  const inputListConRef = useRef<ImperativeRefInput>(null);
  const inputRoadRef = useRef<ImperativeRefInput>(null);
  const inputDateRef = useRef<ImperativeRefCalendarInput>(null);
  const inputExpedientRef = useRef<ImperativeRefInputExpedient>(null);
  const inputIndexRef = useRef<ImperativeRefInputIndex>(null);
  const inputPKInitRef = useRef<ImperativeRefInputPK>(null);
  const inputPKEndRef = useRef<ImperativeRefInputPK>(null);
  const inputListPredefinedRef = useRef<ImperativeRefSearchableDropdown>(null);
  const hasAutoSelectedConcession = useRef<boolean>(false);
  const isLanscapeTablet = useIsLandscapeTablet();
  const isPortraitTablet = useIsPortraitTablet();

  const showCapitolFilter = useFeatureFlag('filter_show_capitol', true);
  const showSubCapitolFilter = useFeatureFlag('filter_show_subcapitol', true);
  const showPredefinedFilter = useFeatureFlag('filter_show_predefined', true);

  const [expedientDefault, setExpedientDefault] = useState<number | undefined>(
    undefined
  );
  const [indexDefault, setIndexDefault] = useState<number | undefined>(
    undefined
  );
  const [showFilters, setShowFilters] = useState(false);
  // In radio mode we still need a local rowSelection so the table renders normally.
  // In checkbox mode this state is unused — controlled props take over.
  const [radioRowSelection, setRadioRowSelection] = useState<RowSelectionState>({});
  const rowSelection: RowSelectionState =
    controlledRowSelection ?? radioRowSelection;
  const handleRowSelectionChange:
    | ((u: RowSelectionState | ((o: RowSelectionState) => RowSelectionState)) => void)
    = controlledOnRowSelectionChange ?? setRadioRowSelection;
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  // C-11.7: persistido en Redux para mantener coherencia paginador↔cursor
  // al navegar a un detail y volver. Ver hooks/useTableCurrentPage.
  const [currentServerPage, setCurrentServerPage] = useTableCurrentPage(
    tableId as string,
  );
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const initialSubCapitolsOptions = data?.filtersDataOptions?.subCapitols?.map(
    (subCapitol: { name: string; id: string }) => ({
      label: subCapitol.name,
      value: subCapitol.id,
    })
  );

  const initialRoadOptions = useMemo(
    () =>
      data?.filtersDataOptions?.roads?.map(
        (road: { name: string; id: string }) => ({
          label: road.name,
          value: road.id,
        })
      ),
    [data?.filtersDataOptions?.roads]
  );

  const concessionOptions = useMemo(
    () =>
      data?.filtersDataOptions?.concessions?.map(
        (concession: { name: string; id: string; roadIDs?: string[] }) => ({
          label: concession.name,
          value: concession.id,
          roadIDs: concession.roadIDs,
        })
      ),
    [data?.filtersDataOptions?.concessions]
  );

  const [roadOptions, setRoadOptions] = useState(initialRoadOptions);

  const [subCapitolOptions, setSubCapitolOptions] = useState(
    initialSubCapitolsOptions
  );

  const [selectorCursor, setSelectorCursor] = useMirroredCursor<{
    description: string | undefined; // filtro por descripción (texto libre)
    take: number;
    cursor: string;
    fromDate: string | undefined;
    toDate: string | undefined;
    capitolId: string | undefined;
    subCapitolId: string | undefined;
    predefinedOrderId: string | undefined;
    indexType: number | undefined;
    indexExpedition: number | undefined;
    classification: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat with cursor shape)
    states: string[] | undefined; // multi-select estado (Plan 2 Tarea: filtro estado multi-select)
    roadId: string | undefined;
    concessionId: string | undefined;
    userId: string | undefined; // legacy single (kept for back-compat)
    userIds: string[] | undefined; // multi-select usuario (Plan 3 Tarea: filtro usuario multi-select)
    containerTypes: string[] | undefined; // multi-select "Origen" (Fase 4 nav Trabajo/Revisión)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador" (Plan Task 6)
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    sortField: string | undefined;
    orderBy: 'asc' | 'desc' | undefined;
    onlyFlagged: true | undefined; // «Solo marcadas»: true o undefined, nunca false
  }>(globalCursor);

  const [selectorData, setSelectorData] = useState<{
    description: string | undefined; // filtro por descripción (texto libre)
    fromDate: string | undefined;
    toDate: string | undefined;
    capitolId: string | undefined;
    subCapitolId: string | undefined;
    predefinedOrderId: string | undefined;
    indexType: number | undefined;
    indexExpedition: number | undefined;
    state: string | undefined; // legacy single (kept for back-compat)
    states: string[] | undefined; // multi-select estado
    roadId: string | undefined;
    concessionId: string | undefined;
    userId: string | undefined; // legacy single (kept for back-compat)
    userIds: string[] | undefined; // multi-select usuario
    containerTypes: string[] | undefined; // multi-select "Origen" (Fase 4 nav Trabajo/Revisión)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador" (Plan Task 6)
    classification: string | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
  }>({
    description: undefined,
    fromDate: undefined,
    toDate: undefined,
    capitolId: undefined,
    subCapitolId: undefined,
    predefinedOrderId: undefined,
    indexType: undefined,
    indexExpedition: undefined,
    state: undefined,
    states: undefined,
    roadId: undefined,
    concessionId: undefined,
    userId: undefined,
    userIds: undefined,
    containerTypes: undefined,
    includeInactive: undefined,
    classification: undefined,
    pkInitKm: undefined,
    pkInitMeter: undefined,
    pkEndKm: undefined,
    pkEndMeter: undefined,
  });

  // Tarea #11 — page-size selector. Activo en el listado principal de
  // Multimedia (ambas pages comparten `tableIds.MULTIMEDIA`). N+1 audit:
  // tabla ligera → mantenemos [25, 50, 100]. `liveFilters: selectorData`
  // para respetar filtros del form aún no comprometidos a Redux al cambiar take.
  const { takePagination, paginationProps } = useServerPageSize({
    tableId: tableId as string,
    enabled: tableId === tableIds.MULTIMEDIA,
    selectorCursor,
    liveFilters: selectorData as unknown as Record<string, unknown>,
    setCurrentServerPage,
    onReFetchData,
  });

  const [sizeTag, setSizeTag] = useState<size>(
    isPortraitTablet ? 'circle' : 'small'
  );

  const roleActive = data.roleActive;

  // NOTE: Multimedia does NOT apply default state filter
  // All multimedia records should be visible without state filtering
  // @see BUG #9 in TRACKING_BUGS_TESTING_2026.md

  // Fase 4 nav Trabajo/Revisión (2026-07-12) — la columna+filtro "Origen" es
  // redundante en las pestañas CONTEXTUALES (campaña/RV). Se deriva del
  // `tableId` (no una prop nueva) — mismo criterio que `TABLE_ENTITY_TYPES`.
  const isContainerScopedTable = CONTAINER_SCOPED_TABLE_IDS.has(
    tableId as string
  );

  const handleClick = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  useEffect(() => {
    isLanscapeTablet ? setShowFilters(false) : setShowFilters(true);
  }, [isLanscapeTablet]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    containerType: !isContainerScopedTable,
  });

  useLayoutEffect(() => {
    setSizeTag(isPortraitTablet ? 'circle' : 'small');
  }, [isPortraitTablet]);

  useEffect(() => {
    setColumnVisibility(prev => ({
      ...prev,
      predefinedOrder: showPredefinedFilter,
      capitol: showCapitolFilter,
      subCapitol: showSubCapitolFilter,
    }));
  }, [showPredefinedFilter, showCapitolFilter, showSubCapitolFilter]);

  const callbackSetRoid = useCallback(async () => {
    let classification;
    if (selectorCursor?.classification) {
      const classificationRaw = await client.query({
        query: GET_CLASSIFICATION_BY_ID_OR_ROID,
        variables: {
          id: selectorCursor?.classification,
        },
      });
      classification =
        classificationRaw?.data?.classification?.classificationCode;
    }
    setExpedientDefault(
      selectorCursor?.indexExpedition !== undefined &&
        selectorCursor?.indexExpedition >= 0
        ? selectorCursor.indexExpedition
        : undefined
    );
    setIndexDefault(
      selectorCursor?.indexType !== undefined && selectorCursor?.indexType >= 0
        ? selectorCursor.indexType
        : undefined
    );
  }, [selectorCursor]);

  useEffect(() => {
    callbackSetRoid();
  }, [selectorCursor]);

  const columnHelper = createColumnHelper<dataTable>();
  const translatedColumnNames = useMemo(() => getColumnNames(t), [t]);

  // Ordenación server-side: campo activo y dirección derivados del cursor Redux
  const activeSortField = selectorCursor?.sortField ?? DEFAULT_SORT_FIELD;
  const activeSortColumnId =
    (Object.keys(SORTABLE_COLUMNS) as (keyof typeof SORTABLE_COLUMNS)[]).find(
      (k) => SORTABLE_COLUMNS[k] === activeSortField,
    ) ?? 'registerInitDate';
  const sortDirection: 'asc' | 'desc' =
    selectorCursor?.orderBy === 'asc' ? 'asc' : 'desc';

  const onSortColumn = useCallback(
    (sortField: string) => {
      const isActive = activeSortField === sortField;
      const nextOrderBy: 'asc' | 'desc' =
        isActive && sortDirection === 'desc' ? 'asc' : 'desc';
      // Canonicalización: default (registerInitDate + desc) viaja sin sortField
      const isDefault =
        sortField === DEFAULT_SORT_FIELD && nextOrderBy === 'desc';
      const dataCursor = {
        sortField: isDefault ? undefined : sortField,
        orderBy: nextOrderBy,
        cursor: '',
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setCurrentServerPage(1);
      onReFetchData?.();
    },
    [activeSortField, sortDirection, dispatch, tableId, setCurrentServerPage, onReFetchData],
  );

  const sortableHeader = useCallback(
    (columnId: keyof typeof SORTABLE_COLUMNS) => () => (
      <SortableHeader
        label={translatedColumnNames[columnId as keyof typeof translatedColumnNames]?.label ?? columnId}
        active={activeSortColumnId === columnId}
        direction={sortDirection}
        onToggle={() => onSortColumn(SORTABLE_COLUMNS[columnId])}
      />
    ),
    [translatedColumnNames, activeSortColumnId, sortDirection, onSortColumn],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <InputCheckContainer>
            <CheckBoxStyled
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          </InputCheckContainer>
        ),
        cell: ({ row }) => (
          <InputCheckContainer
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <CheckBoxStyled
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </InputCheckContainer>
        ),
      }),
      // Banderitas (spec 2026-10-22 § 8.2): justo detrás de la selección y
      // solo si la pantalla la pide.
      ...(showFlagColumn
        ? [
            createFlagColumn<dataTable>(
              getFlagOrderId),
          ]
        : []),
      columnHelper.accessor(getColumnId(translatedColumnNames.state.id), {
        header: sortableHeader('state'),
        cell: (info) => (
          <SpecialTag sizeTag={sizeTag} variant={info.getValue() as variant} />
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.roid.id), {
        header: sortableHeader('roid'),
        cell: (info) => (
          <TextCell size={'S'}>
            {formatRoidForDisplay(info.getValue())}
          </TextCell>
        ),
        filterFn: 'roidFilter' as FilterFnOption<Order>,
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.registerInitDate.id),
        {
          header: sortableHeader('registerInitDate'),
          cell: (info) => (
            <TextCell size={'S'}>
              {format(
                new Date(info.getValue() as string),
                'dd/MM/yyyy - HH:mm'
              )}
            </TextCell>
          ),
          filterFn: 'dateFilter' as FilterFnOption<dataTable>,
        }
      ),
      columnHelper.accessor(getColumnId(translatedColumnNames.road.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.road.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.capitol.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.capitol.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.subCapitol.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.subCapitol.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.predefinedOrder.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.predefinedOrder.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string ?? ''}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkInit.id), {
        header: sortableHeader('pkInit'),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkEnd.id), {
        header: sortableHeader('pkEnd'),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      ...(data?.filtersDataOptions?.users?.length
        ? [
            columnHelper.accessor(getColumnId(translatedColumnNames.user.id), {
              header: () => (
                <TextHeader size={'XS'} colorText={'greyDark'}>
                  {translatedColumnNames.user.label}
                </TextHeader>
              ),
              cell: (info) => (
                <TextCell size={'S'}>
                  {(info.getValue() as string) || ''}
                </TextCell>
              ),
            }),
          ]
        : []),
      columnHelper.accessor(getColumnId(translatedColumnNames.description.id), {
        header: sortableHeader('description'),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      // Fase 4 nav Trabajo/Revisión (2026-07-12) — columna "Origen"
      // (containerType traducido con la label EXISTENTE `common:orderTypes.*`,
      // servido directo por el backend con fallback a `type` en Orders D0 —
      // ver gql/fragments/orders.ts + gql/selectors/multimedia.ts). Oculta
      // por defecto en pestañas contextuales vía `columnVisibility.origin`
      // (gating por tableId).
      columnHelper.accessor(getColumnId(translatedColumnNames.containerType.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.containerType.label}
          </TextHeader>
        ),
        cell: (info) => {
          const value = info.getValue() as string | undefined;
          return (
            <TextCell size={'S'}>
              {value ? t(`common:orderTypes.${value}`) : ''}
            </TextCell>
          );
        },
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.creationDate.id),
        {
          header: sortableHeader('creationDate'),
          cell: (info) => (
            <TextCell size={'S'}>
              {format(
                new Date(info.getValue() as string),
                'dd/MM/yyyy - HH:mm'
              )}
            </TextCell>
          ),
        }
      ),
    ],
    [
      isPortraitTablet,
      translatedColumnNames,
      data?.filtersDataOptions?.users,
      t,
      sortableHeader,
      showFlagColumn,
      getFlagOrderId,
    ]
  );

  // Tarea #11 — Sincronizar TanStack pageSize con `takePagination` (server).
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize: takePagination,
    }),
    [pageIndex, takePagination]
  );

  const filterFns = {
    roidFilter: roidFilterFunction,
    dateFilter: dateFilterFunction,
  };

  const table = useReactTable({
    getRowId: (row: any) => row.id,
    data: dataAll,
    columns,
    state: {
      rowSelection,
      columnFilters,
      pagination,
      columnVisibility,
    },
    filterFns,
    manualSorting: true,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    onPaginationChange: setPagination,
    onRowSelectionChange: handleRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getPaginationRowModel: getPaginationRowModel(),
  });

  // In radio mode, sync the parent's setRowState with selected IDs.
  // In checkbox mode the parent owns the source-of-truth via usePersistentSelection.
  useEffect(() => {
    if (isCheckboxMode || !setRowState) return;
    const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    setRowState(selectedIds);
  }, [rowSelection, setRowState, isCheckboxMode]);

  const capitolOptions = useCallback(() => {
    return data?.filtersDataOptions?.capitols.map((capitol) => ({
      label: capitol.name,
      value: capitol.id,
      subCapitolsIDs: capitol.subCapitolsIDs,
    }));
  }, [data?.filtersDataOptions?.capitols]);

  const classificationOptions = useCallback(() => {
    return data?.filtersDataOptions?.classifications
      ?.slice()
      ?.sort((a, b) => (a.classificationCode ?? 0) - (b.classificationCode ?? 0))
      ?.map((classification) => ({
        label: `${classification.classificationCode} - ${classification.name}`,
        value: classification.id,
      }));
  }, [data?.filtersDataOptions?.classifications]);

  // Plan Task 6: cuando `includeInactive=false` pero el cursor ya tiene
  // userIds seleccionados que NO están en la base activa (porque están
  // inactivos), se mergean como missing options para que los chips
  // sigan renderizando (edge case 7.1 del spec).
  const userOptions = useCallback(
    () => {
      const base = filterAndSortMultimediaUsers(
        data?.filtersDataOptions?.users,
        selectorCursor?.includeInactive ?? false,
      );
      const selectedIds = selectorCursor?.userIds ?? [];
      const missingIds = selectedIds.filter(
        (id: string) => !base.find((o) => o.value === id),
      );
      if (missingIds.length === 0) return base;
      const allUsers = data?.filtersDataOptions?.users ?? [];
      const missingOptions = missingIds
        .map((id: string) => allUsers.find((u: { id: string }) => u.id === id))
        .filter((u: unknown): u is { id?: string; name?: string; surName?: string; isActive?: boolean } => Boolean(u))
        .map((u) => ({
          label:
            u?.isActive === false
              ? `${u?.name ?? ''} ${u?.surName ?? ''} (inactivo)`.trim()
              : `${u?.name ?? ''} ${u?.surName ?? ''}`.trim(),
          value: u?.id ?? '',
        }));
      return [...base, ...missingOptions];
    },
    [
      data?.filtersDataOptions?.users,
      selectorCursor?.includeInactive,
      selectorCursor?.userIds,
    ]
  );

  const predefinedOrderOptions = useMemo(() => {
    return data?.filtersDataOptions?.predefinedOrders?.map(
      (predefined: { id: string; name: string }) => ({
        label: predefined.name,
        value: predefined.id,
      })
    );
  }, [data?.filtersDataOptions?.predefinedOrders]);

  const onChangeInputExpedient = useCallback<onChangeInputExpedientTypes>(
    (value) => {
      setExpedientDefault(value);
      setSelectorData((prevData) => ({
        ...prevData,
        indexExpedition: value,
      }));
    },
    []
  );

  const onChangeInputIndex = useCallback<onChangeInputIndexTypes>((value) => {
    setIndexDefault(value);
    setSelectorData((prevData) => ({
      ...prevData,
      indexType: value,
    }));
  }, []);

  const onChangePKInit = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkInitKm: km, pkInitMeter: meter }));
    },
    []
  );

  const onChangePKEnd = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkEndKm: km, pkEndMeter: meter }));
    },
    []
  );

  const onChangeDateInput = useCallback<onChangeInputType>(
    (range) => {
      if (range.to) {
        range.to.setHours(23);
        range.to.setMinutes(59);
        range.to.setSeconds(59);
        range.to.setMilliseconds(999);
      }
      const fromDateIso = formatISO(range.from as Date);
      const toDateIso = formatISO(range.to as Date);
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor.cursor,
        isMultimedia: true,
        indexType: selectorData.indexType ?? selectorCursor.indexType,
        indexExpedition:
          selectorData.indexExpedition ?? selectorCursor.indexExpedition,
        fromDate: fromDateIso,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        toDate: toDateIso,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        sortField: selectorCursor?.sortField,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        classification:
          selectorData.classification ?? selectorCursor.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.fromDate = fromDateIso;
        newData.toDate = toDateIso;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData.indexType,
      selectorData.indexExpedition,
      selectorData.capitolId,
      selectorData.subCapitolId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.roadId,
      selectorData?.classification,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedClassification = (value: option) => {
    setSelectorData((prevData) => {
      const newData = prevData;
      newData.classification = value.value;
      return newData;
    });
  };

  const onChangeSelectedPredefinedOrder = useCallback(
    (value: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        isMultimedia: true,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        predefinedOrderId: value.value,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        sortField: selectorCursor?.sortField,
        states: selectorData?.states ?? selectorCursor?.states,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        predefinedOrderId: value.value,
      }));
    },
    [selectorData, selectorCursor, dispatch, tableId]
  );

  const onChangeSelectedCapitolGroup = useCallback(
    async (option: option) => {
      const {
        data: { readSubCapitols },
      } = await client.query({
        query: GET_SUB_CAPITOLS_BY_IDS,
        variables: {
          ids: option.subCapitolsIDs,
        },
        fetchPolicy: 'cache-first',
      });
      const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setSubCapitolOptions(
        sortedSubCapitols?.map((road: { id: string; name: string }) => ({
          value: road.id,
          label: road.name,
        }))
      );
      const dataCursor = {
        take: selectorCursor.take,
        isMultimedia: true,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        capitolId: option.value,
        subCapitolId: undefined,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        sortField: selectorCursor?.sortField,
        states: selectorData?.states ?? selectorCursor?.states,
        classification:
          selectorData.classification ?? selectorCursor.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      log('🔄 onChangeCapitol - dataCursor (isMultimedia=%s):', dataCursor.isMultimedia, dataCursor);
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.capitolId = option.value;
        newData.subCapitolId = undefined;
        return newData;
      });

      inputListSubcapitolRef.current?.clearSelected();
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.roadId,
    ]
  );

  const onChangeSelectedSubcapitolGroup = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        isMultimedia: true,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId: option.value,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        sortField: selectorCursor?.sortField,
        states: selectorData?.states ?? selectorCursor?.states,
        classification:
          selectorData?.classification ?? selectorCursor.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      log('🔄 onChangeSubcapitol - dataCursor (isMultimedia=%s):', dataCursor.isMultimedia, dataCursor);
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.subCapitolId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.states,
      selectorData?.userIds,
    ]
  );

  const onChangeSelectedStates = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by `keyArgs: [..., 'states']`
      // produces the same key for the same selection regardless of click order.
      const sorted = [...values].sort();
      const nextStates = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        isMultimedia: true,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        sortField: selectorCursor?.sortField,
        states: nextStates,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      log('🔄 onChangeStates - dataCursor (isMultimedia=%s):', dataCursor.isMultimedia, dataCursor);
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        states: nextStates,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.roadId,
      selectorData?.userIds,
      dispatch,
      tableId,
    ]
  );

  const onChangeSelectedUserIds = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by `keyArgs: [..., 'userIds']`
      // produces the same key for the same selection regardless of click order.
      const sorted = [...values].sort();
      const nextUserIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        isMultimedia: true,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        sortField: selectorCursor?.sortField,
        states: selectorData?.states ?? selectorCursor?.states,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        userIds: nextUserIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      log('🔄 onChangeUserIds - dataCursor (isMultimedia=%s):', dataCursor.isMultimedia, dataCursor);
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        userIds: nextUserIds,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.states,
      selectorData?.roadId,
      selectorData?.containerTypes,
      dispatch,
      tableId,
    ]
  );

  // Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen" (containerType:
  // generic|campaign|surveillanceRoute). Mismo patrón que
  // `onChangeSelectedUserIds` — canonicaliza con sort estable. Solo se monta
  // en la tabla GLOBAL (ver gating en el render).
  const onChangeSelectedContainerTypes = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextContainerTypes = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        isMultimedia: true,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: nextContainerTypes,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        containerTypes: nextContainerTypes,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.states,
      selectorData?.roadId,
      selectorData?.userIds,
      dispatch,
      tableId,
    ]
  );

  // Plan Task 6: toggle "Incluir inactivos en Creador".
  // Solo expone usuarios desactivados en el dropdown Creador; NO filtra
  // resultados del listado. Persiste en el cursor (Redux) y dispara
  // onReFetchData via useEffect aguas abajo (mismo patrón que otros
  // filtros multi-select).
  const onChangeIncludeInactive = useCallback(
    (next: boolean) => {
      const dataCursor = {
        take: selectorCursor.take,
        isMultimedia: true,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        sortField: selectorCursor?.sortField,
        states: selectorData?.states ?? selectorCursor?.states,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        includeInactive: next,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        includeInactive: next,
      }));
    },
    [selectorCursor, selectorData, dispatch, tableId],
  );

  // «Solo marcadas» (spec 2026-10-22 § 8.3). Solo guarda en el cursor —el
  // reducer hace merge, así que basta con su clave— y la búsqueda la lanza
  // Buscar. Desmarcado es `undefined`, nunca `false`: así la caché de Apollo no
  // se parte en dos claves para «sin filtro». `selectorData` no lleva la clave
  // (arranca con un objeto propio, no con el cursor), así que el selector de
  // tamaño de página la toma del reflejo del cursor, que ya queda al día.
  const onChangeOnlyFlagged = useCallback(
    (next: boolean) => {
      dispatch(
        setCursor(tableId as string, { onlyFlagged: next ? true : undefined })
      );
    },
    [dispatch, tableId]
  );

  const onClearValue = useCallback(() => {
    console.log('[MultimediaCheckBoxTable][onClearValue] BEFORE — selectorCursor:', selectorCursor, '| selectorData:', selectorData, '| globalCursor:', globalCursor);
    inputDateRef.current?.clearSelected();
    inputExpedientRef.current?.clearSelected();
    inputIndexRef.current?.clearSelected();
    inputPKInitRef.current?.clearSelected();
    inputPKEndRef.current?.clearSelected();
    inputListCapitolRef.current?.clearSelected();
    inputListSubcapitolRef.current?.clearSelected();
    inputListPredefinedRef.current?.clearSelected();
    inputStateRef.current?.clearSelected();
    inputRoadRef.current?.clearSelected();
    inputListClassificationRef.current?.clearSelected();
    inputListUserRef.current?.clearSelected();
    inputContainerTypeRef.current?.clearSelected();
    setColumnFilters([]);

    // Check if there's only one concession
    const hasOnlyOneConcession = concessionOptions && concessionOptions.length === 1;
    const onlyConcessionId = hasOnlyOneConcession ? concessionOptions[0].value : undefined;

    // If there's only one concession, keep it selected
    // The useEffect will handle re-filtering roads automatically when concessionId changes
    if (!hasOnlyOneConcession) {
      inputListConRef.current?.clearSelected();
      hasAutoSelectedConcession.current = false;
    }

    const dataCursor = {
      take: takePagination,
      cursor: '',
      isMultimedia: true,
      indexType: undefined,
      indexExpedition: undefined,
      fromDate: undefined,
      toDate: undefined,
      orderBy: 'desc' as const,
      sortField: undefined,
      capitolId: undefined,
      roadId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      userId: undefined,
      userIds: undefined,
      containerTypes: undefined,
      includeInactive: undefined,
      subCapitolId: undefined,
      predefinedOrderId: undefined,
      state: undefined,
      states: undefined,
      classification: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      // Explícito: el reducer hace merge y omitirlo conservaría el filtro.
      onlyFlagged: undefined,
    };
    console.log('[MultimediaCheckBoxTable][onClearValue] ▶️ dataCursor.concessionId:', dataCursor.concessionId, '| hasOnlyOneConcession:', hasOnlyOneConcession);
    dispatch(setCursor(tableId as string, dataCursor));
    console.log('[MultimediaCheckBoxTable][onClearValue] ▶️ DISPATCH hecho, ahora llama onReFetchData...');
    // Fix: Create new object instead of mutating to trigger useEffect
    setSelectorCursor((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      fromDate: undefined,
      toDate: undefined,
      capitolId: undefined,
      subCapitolId: undefined,
      predefinedOrderId: undefined,
      state: undefined,
      states: undefined,
      roadId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      userId: undefined,
      userIds: undefined,
      containerTypes: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      sortField: undefined,
      orderBy: 'desc' as const,
      onlyFlagged: undefined,
    }));
    setSelectorData((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      fromDate: undefined,
      toDate: undefined,
      capitolId: undefined,
      subCapitolId: undefined,
      predefinedOrderId: undefined,
      state: undefined,
      states: undefined,
      roadId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      userId: undefined,
      userIds: undefined,
      containerTypes: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
    }));
    setCurrentServerPage(1);
    onReFetchData?.();
    // Tarea #11 fix: `takePagination` debe estar en deps para evitar stale closure.
  }, [concessionOptions, initialRoadOptions, onChangeInputExpedient, onChangeInputIndex, takePagination]);

  // Columnas que ofrece el desplegable «Columnas»: todas menos la de selección
  // (la primera) y las que no se pueden ocultar (la bandera, § 8.2). Sin
  // bandera queda igual que el `.slice(1)` de siempre.
  const hideableColumns = table
    .getAllColumns()
    .slice(1)
    .filter((column) => column.getCanHide());

  const hiddenColumns: number = Object.values(
    columnVisibility as object
  ).reduce((count, currentValue) => (!currentValue ? count + 1 : count), 0);

  // commonColumns definition removed

  // commonLabels definition removed

  const fetchOrder = useCallback(
    async (id: string, take = takePagination) => {
      const dataSelector = {
        take,
        isMultimedia: true,
        cursor: id,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        sortField: selectorCursor?.sortField,
        states: selectorData?.states ?? selectorCursor?.states,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };

      if (take > 0) {
        const lastOrder = dataAll[dataAll.length - 1].id;
        if (lastOrder) {
          dispatch(setCursor(tableId as string, dataSelector));
        }
      } else {
        const firstOrder = dataAll[0].id;

        if (firstOrder) {
          dispatch(setCursor(tableId as string, dataSelector));
        }
      }

      return dataSelector;
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.states,
      selectorData?.roadId,
      selectorData?.userIds,
      dataAll,
      takePagination,
    ]
  );

  const onChangeSelectedConcession = useCallback(
    async (option: option) => {
      const { data } = await client.query({
        query: GET_ROADS_BY_IDS,
        variables: {
          ids: option.roadIDs,
        },
      });

      setRoadOptions(
        data?.readRoads?.map((road: { id: string; name: string }) => ({
          value: road.id,
          label: road.name,
        }))
      );
      const dataCursor = {
        take: selectorCursor.take,
        isMultimedia: true,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: undefined,
        concessionId: option.value,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        sortField: selectorCursor?.sortField,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      log('🔄 onChangeConcession - dataCursor (isMultimedia=%s):', dataCursor.isMultimedia, dataCursor);
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.concessionId = option.value;
        newData.roadId = undefined;
        return newData;
      });

      inputRoadRef.current?.clearSelected();
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.userIds,
    ]
  );

  // Auto-select the only concession if there's only one available and filter roads
  // Uses spread of globalCursor to preserve all fields (including isMultimedia)
  useEffect(() => {
    if (
      concessionOptions &&
      concessionOptions.length === 1 &&
      !selectorCursor.concessionId &&
      !hasAutoSelectedConcession.current
    ) {
      const onlyConcession = concessionOptions[0];
      hasAutoSelectedConcession.current = true;
      log('🔄 auto-concession - globalCursor:', globalCursor);
      const dataCursor = {
        ...globalCursor,
        concessionId: onlyConcession.value,
      };
      log('🔄 auto-concession - dataCursor (isMultimedia=%s):', dataCursor.isMultimedia, dataCursor);
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorCursor((prev: any) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
    }
  }, [concessionOptions, selectorCursor.concessionId]);

  // Filter roads if concessionId is already in cursor (on mount or navigation)
  // Also re-filter when data changes (after revalidate)
  useEffect(() => {
    console.log('[MultimediaCheckBoxTable][useEffect roadFilter] 🛣️ EJECUTANDO — selectorCursor.concessionId:', selectorCursor.concessionId, '| concessionOptions:', concessionOptions?.length, '| initialRoadOptions:', initialRoadOptions?.length);
    const filterRoadsByConcessionOnMount = async () => {
      if (
        selectorCursor.concessionId &&
        concessionOptions &&
        concessionOptions.length > 0
      ) {
        const selectedConcession = concessionOptions.find(
          (c) => c.value === selectorCursor.concessionId
        );
        if (selectedConcession && selectedConcession.roadIDs) {
          const { data } = await client.query({
            query: GET_ROADS_BY_IDS,
            variables: {
              ids: selectedConcession.roadIDs,
            },
          });

          const filteredRoads = data?.readRoads?.map(
            (road: { id: string; name: string }) => ({
              value: road.id,
              label: road.name,
            })
          );

          console.log('[MultimediaCheckBoxTable][useEffect roadFilter] 🛣️ ✅ setRoadOptions FILTRADO:', filteredRoads?.length, 'roads');
          setRoadOptions(filteredRoads || []);
        }
      } else if (!selectorCursor.concessionId && initialRoadOptions) {
        // If no concession is selected, restore all roads
        console.log('[MultimediaCheckBoxTable][useEffect roadFilter] 🛣️ ❌ SIN concessionId → restaurando TODAS las roads:', initialRoadOptions?.length);
        setRoadOptions(initialRoadOptions);
      }
    };

    filterRoadsByConcessionOnMount();
  }, [selectorCursor.concessionId, concessionOptions, initialRoadOptions]);

  const onChangeSelectedRoad = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        isMultimedia: true,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        sortField: selectorCursor?.sortField,
        roadId: option.value,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        states: selectorData?.states ?? selectorCursor?.states,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      log('🔄 onChangeRoad - dataCursor (isMultimedia=%s):', dataCursor.isMultimedia, dataCursor);
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.roadId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.roadId,
      dataAll,
    ]
  );

  const nextPagination = useCallback(async () => {
    // Do NOT reset selection — usePersistentSelection keeps it across pages
    // Clamp defensivo contra bug off-by-one (backend/cache hasNextPage true en última real).
    const pageCount = Math.max(
      1,
      Math.ceil((data?.totalCount ?? 0) / takePagination),
    );
    if (
      dataAll.length >= 1 &&
      data?.pageInfo.hasNextPage &&
      currentServerPage < pageCount
    ) {
      setCurrentServerPage((prev) => Math.min(prev + 1, pageCount));
      await fetchOrder(dataAll[dataAll.length - 1].id as string);
      onReFetchPagination();
    }
  }, [dataAll, data, fetchOrder, takePagination, currentServerPage]);

  const backPagination = useCallback(async () => {
    // Do NOT reset selection — usePersistentSelection keeps it across pages
    if (dataAll.length >= 1 && data?.pageInfo.hasPreviousPage) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchOrder(dataAll[0].id as string, -takePagination);
      onReFetchPagination();
    }
  }, [data, dataAll, fetchOrder, takePagination]);

  const handleSearch = async () => {
    console.log('[MultimediaCheckBoxTable][handleSearch] ▶️ INICIO — selectorCursor.concessionId:', selectorCursor?.concessionId, '| selectorData.concessionId:', selectorData?.concessionId);
    const dataCursor = {
      take: takePagination,
      isMultimedia: true,
      cursor: '',
      indexType: selectorData?.indexType ?? selectorCursor?.indexType,
      indexExpedition:
        selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
      subCapitolId: selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
      capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
      predefinedOrderId:
        selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
      toDate: selectorData?.toDate ?? selectorCursor?.toDate,
      orderBy: selectorCursor?.orderBy ?? 'desc',
      sortField: selectorCursor?.sortField,
      states: selectorData?.states ?? selectorCursor?.states,
      roadId: selectorData?.roadId ?? selectorCursor?.roadId,
      concessionId:
        selectorData?.concessionId ?? selectorCursor?.concessionId,
      userIds: selectorData?.userIds ?? selectorCursor?.userIds,
      containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
      includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
      classification:
        selectorData?.classification ?? selectorCursor?.classification,
      pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
      pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
      pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
      pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      description:
        (selectorData.description ?? selectorCursor.description) || undefined,
    };
    console.log('[MultimediaCheckBoxTable][handleSearch] ▶️ dataCursor.concessionId:', dataCursor.concessionId);
    log('🔄 handleSearch - dataCursor (isMultimedia=%s):', dataCursor.isMultimedia, dataCursor);
    dispatch(setCursor(tableId as string, dataCursor));
    console.log('[MultimediaCheckBoxTable][handleSearch] ▶️ DISPATCH hecho, ahora llama onReFetchData...');
    setCurrentServerPage(1);
    onReFetchData?.();
  };

  const filterContainerRef = useRef<HTMLDivElement>(null);
  useFilterKeyboardShortcuts(filterContainerRef, handleSearch, onClearValue, isLoadingData as boolean);

  return (
    <ContainerStyled id={idCss}>
      {isLoadingData && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <FiltersWithLoadingStyled isLoading={isLoadingData}>
        <SubContainerHead>
          <Title component={'h2'} variant={'semiBold'} size={'M'}>
            {titleText}
          </Title>
          {isLanscapeTablet && (
            <ShowFiltersButton isBlack={showFilters} onClick={handleClick}>
              <ArrowBottomStyled isRotate={showFilters} />
            </ShowFiltersButton>
          )}
        </SubContainerHead>
        {showFilters && (
          <SubContainerFilters ref={filterContainerRef}>
            <InputExpedient
              label={t('headers.expedient')}
              disabled={isLoadingData}
              expedient={expedientDefault}
              onChangeInputExpedient={onChangeInputExpedient}
              ref={inputExpedientRef}
            />

            <InputIndex
              label={t('headers.index')}
              disabled={isLoadingData}
              index={indexDefault}
              onChangeInputIndex={onChangeInputIndex}
              ref={inputIndexRef}
            />

            <InputPK
              label={t('tables:headers.pkInit')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKInit}
              ref={inputPKInitRef}
            />

            <InputPK
              label={t('tables:headers.pkEnd')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKEnd}
              ref={inputPKEndRef}
            />
            {/* Filtro por descripción: texto libre sobre Order.description.
                Busca por subcadena, sin distinguir acentos ni mayúsculas, y
                exige todas las palabras en cualquier orden (el backend filtra
                contra la copia normalizada `descriptionSearch`, indexada). */}
            <InputText
              label={t('tables:headers.description')}
              typeInput="normal"
              inputSize="S"
              placeholder={t('common:placeholders.writeHere')}
              disabled={isLoadingData}
              value={
                selectorData?.description ?? selectorCursor?.description ?? ''
              }
              onChange={(e) =>
                setSelectorData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            <InputCalendarStyled
              ref={inputDateRef}
              label={t('tables:filters.dateRangeFilter')}
              name={'data'}
              onChangeInput={onChangeDateInput}
              dateRange={
                selectorCursor?.fromDate && selectorCursor?.toDate
                  ? {
                    from: parseISO(selectorCursor?.fromDate),
                    to: parseISO(selectorCursor?.toDate),
                  }
                  : undefined
              }
              disabled={isLoadingData}
            />

            {showCapitolFilter && (
              <SearchableDropdown
                inputSize="L"
                ref={inputListCapitolRef}
                labelText={'Capitol'}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.capitolId ?? ''}
                options={capitolOptions() as Options}
                onChangeSelected={onChangeSelectedCapitolGroup as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}

            {showSubCapitolFilter && (
              <SearchableDropdown
                inputSize="L"
                ref={inputListSubcapitolRef}
                labelText={'Subcapitol'}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.subCapitolId ?? ''}
                options={subCapitolOptions as Options}
                onChangeSelected={
                  onChangeSelectedSubcapitolGroup as onChangeValue
                }
                isDisabled={isLoadingData}
              />
            )}

            {showPredefinedFilter && predefinedOrderOptions && predefinedOrderOptions.length > 0 && (
              <SearchableDropdownStyled
                inputSize="L"
                ref={inputListPredefinedRef}
                labelText={t('forms:workOrder.predefined')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.predefinedOrderId ?? ''}
                options={predefinedOrderOptions as Options}
                onChangeSelected={onChangeSelectedPredefinedOrder as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}

            <SearchableDropdownStyled
              inputSize="L"
              ref={inputListClassificationRef}
              labelText={t('tables:headers.classification')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Default'}
              defaultValue={selectorCursor?.classification ?? ''}
              options={classificationOptions() as Options}
              onChangeSelected={
                onChangeSelectedClassification as onChangeValue
              }
              isDisabled={isLoadingData}
            />

            {userOptions() && userOptions().length > 0 && (
              <MultiSelectDropdownStyled
                inputSize="L"
                ref={inputListUserRef}
                labelText={t('common:labels.creator')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                searchable
                selectedValues={selectorCursor?.userIds ?? []}
                options={userOptions() as Options}
                onChangeSelected={onChangeSelectedUserIds}
                isDisabled={isLoadingData}
              />
            )}

            <MultiSelectDropdownStyled
              inputSize="S"
              ref={inputStateRef}
              labelText={t('tables:headers.status')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              selectedValues={selectorCursor?.states ?? []}
              options={roleActive ? getStateOptionsByRoleAndEntityType(t, roleActive as Role, TABLE_ENTITY_TYPES[tableId as string], listingFiltersByRole(tableId)) : []}
              onChangeSelected={onChangeSelectedStates}
              isDisabled={isLoadingData}
            />

            {/* Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen".
                Oculto en pestañas contextuales (campaña/RV). */}
            {!isContainerScopedTable && (
              <MultiSelectDropdownStyled
                inputSize="S"
                ref={inputContainerTypeRef}
                labelText={t('common:tables.headers.origin')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                selectedValues={selectorCursor?.containerTypes ?? []}
                options={getContainerTypeOptions(t)}
                onChangeSelected={onChangeSelectedContainerTypes}
                isDisabled={isLoadingData}
              />
            )}

            {concessionOptions && concessionOptions.length > 1 && (
              <DropDownInput
                inputSize="XS"
                ref={inputListConRef}
                labelText={t('tables:headers.concession')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                defaultValue={selectorCursor?.concessionId ?? ''}
                options={concessionOptions as Options}
                onChangeSelected={onChangeSelectedConcession as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            <DropDownInput
              inputSize="XS"
              ref={inputRoadRef}
              labelText={t('tables:headers.road')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              defaultValue={selectorCursor?.roadId ?? ''}
              options={roadOptions as Options}
              onChangeSelected={onChangeSelectedRoad as onChangeValue}
              isDisabled={isLoadingData}
            />

            <ContainerButtons>
              <ButtonIconBackground
                variant={'delete'}
                onClick={onClearValue}
                disabled={isLoadingData}
                title={`${t('tables:filters.clearFilters')} (Shift+Enter)`}
              />
              <DropDownVisibilityColumn
                columnNames={Object.fromEntries(hideableColumns.map(c => [c.id, translatedColumnNames[c.id as keyof typeof translatedColumnNames]?.label || c.id]))}
                columns={hideableColumns}
                isDisabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'restart'}
                onClick={() => { setCurrentServerPage(1); onReFetchData?.(); }}
                disabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'search'}
                onClick={handleSearch}
                disabled={isLoadingData}
                title={`${t('buttons:search')} (Enter)`}
              />
            </ContainerButtons>
            <IncludeInactiveToggle
              checked={selectorCursor?.includeInactive ?? false}
              onChange={onChangeIncludeInactive}
              label={t('common:filters.includeInactiveInCreator')}
              isDisabled={isLoadingData}
            />
            {showOnlyFlaggedFilter && (
              <IncludeInactiveToggle
                checked={Boolean(selectorCursor?.onlyFlagged)}
                onChange={onChangeOnlyFlagged}
                label={flagScopeTexts.onlyFlagged}
                isDisabled={isLoadingData}
              />
            )}
          </SubContainerFilters>
        )}
      </FiltersWithLoadingStyled>
      <TableContainerStyled isLoading={isLoadingData}>
        <Table {...props}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    {...exportIgnoreProps(header.column.id)}
                    aria-sort={
                      header.column.id === activeSortColumnId
                        ? sortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </Th>
                ))}
              </tr>
            ))}
          </thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <DoubleTapRow
                  key={row.id}
                  StyledComponent={TrStyled}
                  componentProps={{
                    checked: row.getIsSelected(),
                    indeterminate: !table.getIsSomeRowsSelected(),
                  }}
                  isDisabled={isLoadingData as boolean}
                  onSingleTap={() => {
                    row.getToggleSelectedHandler()(row);
                  }}
                  onDoubleTap={() => {
                    if (row.original?.id) {
                      navigate(`${multimediaBasePath}/${row.original.id}`);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      column={cell.column.id as columnID}
                      hiddenColumns={hiddenColumns}
                      key={cell.id}
                      {...exportIgnoreProps(cell.column.id)}
                    >
                      <>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </>
                    </Td>
                  ))}
                </DoubleTapRow>
              );
            })}
          </Tbody>
        </Table>
      </TableContainerStyled>
      {isCheckboxMode && onSelectAll && onClearSelection && (
        <SelectionBanner
          selectedCount={selectedCount}
          totalCount={data?.totalCount ?? 0}
          pageCount={dataAll.length}
          allPageSelected={
            dataAll.length > 0 &&
            dataAll.every((row) => Boolean(rowSelection[row.id as string]))
          }
          selectAllMode={selectAllMode}
          onSelectAll={onSelectAll}
          onClearSelection={onClearSelection}
        />
      )}
      <PaginationButtons
        pageCount={Math.max(1, Math.ceil((data?.totalCount ?? 0) / takePagination))}
        currentPage={currentServerPage}
        totalCount={data?.totalCount}
        nextPage={nextPagination}
        previousPage={backPagination}
        isDisabled={isLoadingData}
        // Tarea #11: gates aplicados en `useServerPageSize`. En tablet
        // o tableId no-principal, props undefined.
        {...paginationProps}
      />
    </ContainerStyled>
  );
};
